import { z } from 'zod';
/**
 * Automatic agent updates for `/api/v1` (KIR-5191).
 *
 * **Unpublished.** The routes are registered with `schema.hide: true` and
 * listed in `UNPUBLISHED_ROUTES`, and answer the generic 404 while the
 * `agent_auto_update` flag is off for the calling organization. The shapes
 * below are frozen only once the operations join `PUBLISHED_OPERATIONS`.
 *
 * The setting is the same `settings` row the dashboard's Settings >
 * Organization switch writes, through the same domain handler.
 */
/** Read shape of the organization's automatic-update switch. */
export const v1AgentAutoUpdateSettingSchema = z
    .object({
    enabled: z
        .boolean()
        .describe("Whether the organization's agents install Knostic's latest release by themselves. On by default. Machines whose MDM pins a version stay on that version."),
    paused: z
        .boolean()
        .describe('Whether Knostic has paused automatic agent updates for every organization. While `true` no agent is offered an update, whatever `enabled` says, and agents keep their current version.'),
    updatedAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When the switch was last changed. `null` while the default applies.'),
})
    .meta({
    id: 'V1AgentAutoUpdateSetting',
    title: 'Automatic agent updates',
    description: "The organization's automatic agent update switch.",
    examples: [
        { enabled: true, paused: false, updatedAt: '2026-10-01T09:30:00.000Z' },
    ],
});
/** Write shape: the whole setting. */
export const v1AgentAutoUpdateSettingUpdateSchema = z
    .object({
    enabled: z
        .boolean()
        .describe('`true` turns automatic updates on, `false` turns them off.'),
})
    .meta({
    id: 'V1AgentAutoUpdateSettingUpdate',
    title: 'Automatic agent updates update',
    description: 'Turns automatic agent updates on or off.',
    examples: [{ enabled: false }],
});
/** Query of "Try update now": optionally one endpoint of the endpoint user. */
export const v1AgentUpdateCheckQuerySchema = z.object({
    machineId: z
        .string()
        .regex(/^[0-9a-fA-F]{32}$/)
        .optional()
        .describe('Ask only the agent on this endpoint, by the `machineId` running-processes publishes. Omit it to ask every connected agent of the endpoint user. Answers 404 when the endpoint user has no endpoint with this ID in your organization.'),
});
/** Why "Try update now" sent nothing. */
export const v1AgentUpdateCheckSkipReasonSchema = z
    .enum(['off', 'paused'])
    .meta({
    id: 'V1AgentUpdateCheckSkipReason',
    title: 'Agent update check skip reason',
    description: 'Why no agent was asked to check. `off`: automatic agent updates are off for the organization. `paused`: Knostic has paused automatic agent updates for every organization.',
});
/** Result of "Try update now" for one endpoint user. */
export const v1AgentUpdateCheckResultSchema = z
    .object({
    requested: z
        .boolean()
        .describe("Whether the user's connected agents (only the named endpoint's, when `machineId` is given) were asked to check for an update now. `false` when automatic agent updates are off for the organization or paused by Knostic; `reason` says which. Best effort: an agent that is not connected checks at its next scheduled check."),
    reason: v1AgentUpdateCheckSkipReasonSchema
        .nullable()
        .describe('Why nothing was sent. `null` when `requested` is `true`.'),
})
    .meta({
    id: 'V1AgentUpdateCheckResult',
    title: 'Agent update check result',
    description: "The outcome of asking an endpoint user's agents to check for an update now.",
    examples: [{ requested: true, reason: null }],
});
/** Update state of one endpoint's agent. */
export const v1AgentUpdateStateSchema = z
    .enum([
    'up-to-date',
    'updating',
    'pending-reboot',
    'needs-attention',
    'update-failed',
    'pinned-by-mdm',
    'not-available',
    'off',
])
    .meta({
    id: 'V1AgentUpdateState',
    title: 'Agent update state',
    description: "Where an endpoint's agent stands with automatic updates. `up-to-date`: runs the latest version. `updating`: installing it. `pending-reboot`: installed; finishes at the next restart. `needs-attention`: the new version did not reconnect in time, or the agent reported an install it cannot update. `update-failed`: the last install failed. `pinned-by-mdm`: an MDM installs a fixed version. `not-available`: this install cannot update itself. `off`: automatic agent updates are off for the organization.",
});
/** Automatic-update status of one endpoint of an endpoint user. */
export const v1EndpointAgentUpdateStatusSchema = z
    .object({
    machineId: z
        .string()
        .describe('The endpoint, by the `machineId` running-processes publishes. Pass it to "Try update now" to ask only this endpoint\'s agent.'),
    endpointName: z
        .string()
        .nullable()
        .describe("The endpoint's hostname, as the agent last reported it. Null when the endpoint has not reported one."),
    agentVersion: z
        .string()
        .nullable()
        .describe('The agent version the endpoint last reported. Null before its first report.'),
    latestVersion: z
        .string()
        .nullable()
        .describe("Knostic's latest production agent version, which agents update to. The same on every row. Null when it cannot be read right now."),
    autoUpdateEnabled: z
        .boolean()
        .describe("The organization's automatic agent updates switch. The same on every row."),
    paused: z
        .boolean()
        .describe('Whether Knostic has paused automatic agent updates for every organization, as `paused` on the setting. While `true` no agent is offered an update, whatever `autoUpdateEnabled` says, and agents keep their current version. The same on every row.'),
    updateState: v1AgentUpdateStateSchema
        .nullable()
        .describe('Null before the agent first checks for an update.'),
    targetVersion: z
        .string()
        .nullable()
        .describe('The version the last update the agent started was installing. Null when it has started none.'),
    lastUpdateCheckAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When the agent last checked for an update. Null before its first check.'),
    lastUpdateError: z
        .string()
        .nullable()
        .describe('The error of the last failed update, a short code plus detail such as `download_failed`. Null when the last update did not fail.'),
})
    .meta({
    id: 'V1EndpointAgentUpdateStatus',
    title: 'Endpoint agent update status',
    description: "One endpoint's agent version and automatic-update status.",
    examples: [
        {
            machineId: 'e85c251bb2c046fa459a6af3b7c1cee2',
            endpointName: 'stella-mbp.local',
            agentVersion: '3.120.0',
            latestVersion: '3.121.0',
            autoUpdateEnabled: true,
            paused: false,
            updateState: 'update-failed',
            targetVersion: '3.121.0',
            lastUpdateCheckAt: '2026-10-13T10:00:00.000Z',
            lastUpdateError: 'download_failed',
        },
    ],
});
/** Every endpoint of one endpoint user, with its update status. Not paginated. */
export const v1AgentUpdateStatusListResponseSchema = z
    .object({
    data: z
        .array(v1EndpointAgentUpdateStatusSchema)
        .describe("The endpoint user's endpoints seen within your inventory-visibility window. Empty while endpoint identification is off for the organization."),
})
    .meta({
    id: 'V1AgentUpdateStatusListResponse',
    title: 'Agent update status of an endpoint user',
    description: "An endpoint user's endpoints with their agent version and automatic-update status.",
});
//# sourceMappingURL=agent-auto-update.js.map